import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AffiliatedEntityCompositeEntity,
  AppFeatureKey,
  CompanyAffiliationCompositeEntity,
  CompanyAffiliationEntity,
  CompanyAffiliationTypeEntity,
  COUNTRY_FEATURES,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { Observable } from 'rxjs';

@Injectable()
export class CompanyAffiliationService {
  constructor(
    private _analysisApiClientAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
  ) {}

  public companyAffiliationTypeListCache = this._cachingService.create(this, this.fetchCompanyAffiliationTypeList);
  public fetchCompanyAffiliationTypeList(): Observable<CompanyAffiliationTypeEntity[]> {
    return this._analysisApiClientAccess.get<CompanyAffiliationTypeEntity[]>('company-affiliation-type');
  }

  public companyAffiliationListCache = this._cachingService.create(this, this.fetchCompanyAffiliationList);
  public fetchCompanyAffiliationList(
    companyIdentifier: string,
    companyAffiliationTypeIdentifier?: string,
  ): Observable<CompanyAffiliationEntity[]> {
    let params = new HttpParams();
    if (companyAffiliationTypeIdentifier) {
      params = params.append('company_affiliation_type_identifier', companyAffiliationTypeIdentifier);
    }
    return this._analysisApiClientAccess.get<CompanyAffiliationEntity[]>(
      `company/${companyIdentifier}/company-affiliation`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyAffiliationCompositeListCache = this._cachingService.create(
    this,
    this.fetchCompanyAffiliationCompositeList,
  );
  public fetchCompanyAffiliationCompositeList(
    companyIdentifier: string,
    companyAffiliationTypeIdentifier: string,
  ): Observable<CompanyAffiliationCompositeEntity[]> {
    let params = new HttpParams();
    params = params.append('company_affiliation_type_identifier', companyAffiliationTypeIdentifier);
    return this._analysisApiClientAccess.get<CompanyAffiliationCompositeEntity[]>(
      `company/${companyIdentifier}/company-affiliation-composite`,
      { params: params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyAffiliationCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchCompanyAffiliationCompositeCollection,
  );
  public fetchCompanyAffiliationCompositeCollection(
    affiliatedCompanyIdentifier: string,
    affiliatedPersonIdentifier: string,
    companyAffiliationTypeIdentifier: string,
    affiliatedEntitySearchQuery: string,
    latestFlag = true,
    limit = 10,
    offset = 0,
  ): Observable<SimpleCollection<CompanyAffiliationCompositeEntity>> {
    let params = new HttpParams();
    if (affiliatedCompanyIdentifier) {
      params = params.append('affiliated_company_identifier', affiliatedCompanyIdentifier);
    }
    if (affiliatedPersonIdentifier) {
      params = params.append('affiliated_person_identifier', affiliatedPersonIdentifier);
    }
    if (companyAffiliationTypeIdentifier) {
      params = params.append('company_affiliation_type_identifier', companyAffiliationTypeIdentifier);
    }
    if (affiliatedEntitySearchQuery) {
      params = params.append('affiliated_entity_search_query', affiliatedEntitySearchQuery);
    }
    params = params.append('latest_flag', latestFlag);
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this._analysisApiClientAccess.get<SimpleCollection<CompanyAffiliationCompositeEntity>>(
      `/company-affiliation-composite-collection`,
      { params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public affiliatedEntityCompositeCollectionCache = this._cachingService.create(
    this,
    this.fetchAffiliatedEntityCompositeCollection,
  );
  public fetchAffiliatedEntityCompositeCollection(
    companyAffiliationTypeIdentifiers: string[],
    affiliatedEntitySearchQuery: string,
    geoCountryIdentifiers: string[],
    limit = 10,
    offset = 0,
  ): Observable<SimpleCollection<AffiliatedEntityCompositeEntity>> {
    let params = new HttpParams();
    if (companyAffiliationTypeIdentifiers?.length) {
      params = params.append('company_affiliation_type_identifiers', companyAffiliationTypeIdentifiers.join(','));
    }
    if (affiliatedEntitySearchQuery) {
      params = params.append('affiliated_entity_search_query', affiliatedEntitySearchQuery);
    }
    if (geoCountryIdentifiers?.length) {
      params = params.append('geo_country_identifiers', geoCountryIdentifiers.join(','));
    }
    params = params.append('limit', limit);
    params = params.append('offset', offset);
    return this._analysisApiClientAccess.get<SimpleCollection<AffiliatedEntityCompositeEntity>>(
      `/affiliated-entity-composite-collection`,
      { params },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }
}
